import React, { useState, useEffect, useRef } from 'react'
import { Alert } from '@material-ui/lab'
import { Button as MuiButton, MenuItem, Select, Chip } from '@material-ui/core'
import StateMedicaidSearchFields from './SearchViewComponents/StateMedicaidSearchFields'
import StateMedicaidResultsTable from './SearchViewComponents/StateMedicaidResultsTable'
import StateMedicaidPreviousSearches from './SearchViewComponents/StateMedicaidPreviousSearches'
import StateMedicaidSavedRecords from './SearchViewComponents/StateMedicaidSavedRecords'
import {
    stateMedicaidQuickSearch,
    stateMedicaidQuickSearchDemoMode,
    saveStateMedicaidRecord,
    getStateMedicaidSavedRecords,
    getStateMedicaidCoverage,
} from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'
import { useAppContext } from 'context/AppContext'

const BATCH_SIZE = 100
const PAGE_SIZES = [10, 25, 50, 100]

const StateMedicaidSearchView = ({ isQuickSearch, applicantId, prefillTargets }) => {
    const { isDemoMode } = useAppContext()
    const performSearch = isDemoMode ? stateMedicaidQuickSearchDemoMode : stateMedicaidQuickSearch
    const [searchLoading, setSearchLoading] = useState(false)
    const inflightBatches = useRef(0)
    const [savedKeys, setSavedKeys] = useState(new Set())
    const [savedRefresh, setSavedRefresh] = useState(0)
    const [status, setStatus] = useState(null)
    const [lastPayload, setLastPayload] = useState(null)
    const [batches, setBatches] = useState({})
    const [totalRecords, setTotalRecords] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(25)
    const [hasSearched, setHasSearched] = useState(false)
    const [historyRefresh, setHistoryRefresh] = useState(0)
    const [lastSearchStatusId, setLastSearchStatusId] = useState(null)
    // Coverage badge — sourced from the non-admin quicksearch coverage endpoint (any authenticated
    // user can call it). MUST NOT call the admin ingest-status endpoint from here — that 401s
    // non-admins and the JWT interceptor treats that as an expired session, logging the user out.
    const [coverageStates, setCoverageStates] = useState([])

    useEffect(() => {
        let cancelled = false
        getStateMedicaidCoverage()()
            .then(res => {
                if (cancelled) return
                setCoverageStates(res?.stateCodes || [])
            })
            .catch(() => {})
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        if (!applicantId) { setSavedKeys(new Set()); return }
        getStateMedicaidSavedRecords({ applicationId: applicantId })()
            .then(res => setSavedKeys(new Set((res || []).map(r => r.matchedKey).filter(Boolean))))
            .catch(() => {})
    }, [applicantId, savedRefresh])

    const handleSave = ({ meta, record }) => {
        if (!applicantId) return
        const payload = {
            searchId: lastSearchStatusId,
            matchedKey: meta.recordHash,
            matchedName: meta.displayName,
            entityType: meta.isEntity ? 'ENTITY' : 'INDIVIDUAL',
            stateCode: record.state_code || null,
            sourceName: record.source_name || null,
            npi: record.npi || null,
            providerType: record.provider_type || null,
            licenseNumber: record.license_number || null,
            providerNumbers: record.provider_numbers || null,
            suspensionDate: record.suspension_date || null,
            reinstatementDate: record.reinstatement_date || null,
            activePeriodRaw: record.active_period_raw || null,
            dob: record.dob || null,
            address: record.address || null,
            city: record.city || null,
            state: record.state || null,
            zip: record.zip || null,
            isActive: record.is_active,
            notes: null,
        }
        saveStateMedicaidRecord({ applicationId: applicantId, payload })()
            .then(() => {
                Toast({ type: 'success', message: `Saved: ${meta.displayName}` })
                setSavedRefresh(k => k + 1)
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to save record' }))
    }

    const fetchBatch = (payload, batchNum) => {
        inflightBatches.current += 1
        setSearchLoading(true)
        const paginated = { ...payload, page: batchNum, size: BATCH_SIZE }
        return performSearch({ payload: paginated })()
            .then(res => {
                const body = res?.response || res?.responseBody || res
                const records = body?.results || body?.result || []
                const total = body?.result_count ?? body?.resultCount ?? records.length
                setBatches(prev => ({ ...prev, [batchNum]: records }))
                setTotalRecords(total)
                setStatus(res?.status || res?.interactionStatus || null)
                if (batchNum === 0) {
                    setHistoryRefresh(k => k + 1)
                    setLastSearchStatusId(res?.uuid || null)
                }
                return { total, records }
            })
            .catch(() => {
                Toast({ type: 'error', message: 'State Medicaid search failed' })
                return null
            })
            .finally(() => {
                inflightBatches.current = Math.max(0, inflightBatches.current - 1)
                if (inflightBatches.current === 0) setSearchLoading(false)
            })
    }

    const handleSearch = (payload) => {
        setLastPayload(payload)
        setPage(0)
        setBatches({})
        setTotalRecords(0)
        setHasSearched(true)
        fetchBatch(payload, 0)
    }

    const ensureBatchesForPage = (targetPage, targetSize) => {
        if (!lastPayload) return
        const start = targetPage * targetSize
        const end = start + targetSize - 1
        const startBatch = Math.floor(start / BATCH_SIZE)
        const endBatch = Math.floor(end / BATCH_SIZE)
        for (let b = startBatch; b <= endBatch; b++) {
            if (!batches[b] && b * BATCH_SIZE < totalRecords) {
                fetchBatch(lastPayload, b)
            }
        }
    }

    const goToPage = (newPage) => {
        setPage(newPage)
        ensureBatchesForPage(newPage, pageSize)
    }

    const changePageSize = (newSize) => {
        setPageSize(newSize)
        setPage(0)
        ensureBatchesForPage(0, newSize)
    }

    const start = page * pageSize
    const end = start + pageSize
    const startBatch = Math.floor(start / BATCH_SIZE)
    const endBatch = Math.floor((end - 1) / BATCH_SIZE)

    const combined = []
    let combinedStartIndex = startBatch * BATCH_SIZE
    let missingBatch = false
    for (let b = startBatch; b <= endBatch; b++) {
        if (batches[b]) {
            combined.push(...batches[b])
        } else if (b * BATCH_SIZE < totalRecords) {
            missingBatch = true
            break
        }
    }
    const relativeStart = start - combinedStartIndex
    const displayed = combined.slice(relativeStart, relativeStart + pageSize)
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
    const hasResults = totalRecords > 0

    return (
        <>
            <div className='cardStyle mb-3'>
                <div className='d-flex justify-content-between align-items-center flex-wrap' style={{ gap: 8 }}>
                    <h5 className='mb-0'>State Medicaid Exclusions</h5>
                    {coverageStates.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, color: '#555' }}>Coverage:</span>
                            {coverageStates.map(sc => (
                                <Chip
                                    key={sc}
                                    label={sc}
                                    size='small'
                                    style={{ background: 'var(--badge-approved-bg)', color: 'var(--status-approved)', fontWeight: 600 }}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <StateMedicaidSearchFields
                    loading={searchLoading}
                    handleSearch={handleSearch}
                    isQuickSearch={isQuickSearch}
                    isDemoMode={isDemoMode}
                    prefillTargets={prefillTargets}
                    coverageStates={coverageStates}
                />

                {status === 'ERROR' && (
                    <Alert severity='error' className='mt-3'>
                        Search failed. See interaction response for details.
                    </Alert>
                )}
                {hasSearched && !hasResults && !searchLoading && status !== 'ERROR' && (
                    <Alert severity='info' className='mt-3'>
                        No matching exclusions found.
                    </Alert>
                )}
                {hasResults && (
                    <div className='mt-3'>
                        <Alert severity='success'>
                            {totalRecords} exclusion{totalRecords === 1 ? '' : 's'} found
                            &nbsp;·&nbsp; page {page + 1} of {totalPages}
                        </Alert>
                        {missingBatch && searchLoading ? (
                            <div style={{ padding: 24, textAlign: 'center' }}>Loading page…</div>
                        ) : (
                            <StateMedicaidResultsTable
                                records={displayed}
                                onSave={applicantId ? handleSave : undefined}
                                savedKeys={savedKeys}
                                saveEnabled={!!applicantId}
                            />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13 }}>Rows per page:</span>
                                <Select
                                    value={pageSize}
                                    onChange={(e) => changePageSize(Number(e.target.value))}
                                    disabled={searchLoading}
                                    style={{ fontSize: 13 }}
                                >
                                    {PAGE_SIZES.map(n => (
                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MuiButton
                                    size='small'
                                    variant='outlined'
                                    disabled={page <= 0 || searchLoading}
                                    onClick={() => goToPage(page - 1)}
                                >
                                    Prev
                                </MuiButton>
                                <span style={{ fontSize: 13 }}>{page + 1} / {totalPages}</span>
                                <MuiButton
                                    size='small'
                                    variant='outlined'
                                    disabled={page + 1 >= totalPages || searchLoading}
                                    onClick={() => goToPage(page + 1)}
                                >
                                    Next
                                </MuiButton>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Search history is client-scoped (returns rows with application_id IS NULL). Only meaningful on the
                standalone Quick Search page — hide it inside the application detail tab so global searches
                don't appear as if they were run against this application. */}
            {!applicantId && (
                <StateMedicaidPreviousSearches
                    refreshKey={historyRefresh}
                    onViewResult={(res) => {
                        const body = res?.response || res?.responseBody || res
                        const records = body?.results || body?.result || []
                        const total = body?.result_count ?? body?.resultCount ?? records.length
                        setBatches({ 0: records })
                        setTotalRecords(total)
                        setPage(0)
                        setHasSearched(true)
                        setLastSearchStatusId(res?.uuid || null)
                    }}
                />
            )}
            {applicantId && (
                <StateMedicaidSavedRecords
                    applicantId={applicantId}
                    refreshKey={savedRefresh}
                    onRecordDeleted={() => setSavedRefresh(k => k + 1)}
                />
            )}
        </>
    )
}

export default StateMedicaidSearchView
